// One concept a turn, offered when the board makes it true.
//
// §16.3. The table is a **partial order, not a sequence**: `needs` says what
// must come first, `when` decides what is available now, and the table's order
// breaks ties. Two players meet the same concepts in different orders and both
// are right.
//
// **Pure, and no prose.** `nextHint` is a function of a profile, a board and a
// side, so the whole curriculum is testable by replaying a log and asserting
// the *order* concepts were offered in, with no browser and no storage. The
// sentences live in `content/hints/`, looked up by id at render time — a
// translator gets a file of sentences and nobody edits this one to fix a comma.
//
// **`src/engine/rules/` may never import this.** A rule that consulted play history
// would derive two different games from one log. The dependency runs one way,
// and `mechanics.test.ts` asserts it by reflection.

import {
  type Color,
  fileOf,
  FORWARD_FACING,
  KNIGHT,
  PAWN,
  pieceColor,
  pieceType,
  rankOf,
  type Side
} from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { facingAt, pieceAt } from "src/engine/rules/position";
import { canPlaceBelt } from "src/engine/rules/industry/belts";
import {
  harvestRate,
  isFoundry,
  refinesAt,
  machineType
} from "src/engine/rules/industry/machines";
import {
  beltOwnerAt,
  blueprintOwnerAt,
  bufferTotal,
  heapAt,
  liveSquares,
  targetAt
} from "src/engine/rules/industry/state";
import type { MechanicId } from "./mechanics";
import { hasMet, type Profile } from "./profile";

export interface Concept {
  id: MechanicId;
  /**
   * Concepts that must be met before this one is offered.
   *
   * **Only where `when` cannot imply the order**, and in this first table that
   * is nowhere — every entry's `when` already requires the board state its
   * predecessor produces (you cannot be holding ore without having mined, or
   * own a lane without having laid one). A redundant `needs` is worse than an
   * absent one: it gates on a *command count* while `when` gates on the board,
   * and mining and handing off happen automatically as well as on demand — so
   * a player for whom the tick did the work has a board that has moved on and
   * a profile that has not, and the curriculum stalls behind a prerequisite it
   * can see is already done. Kept in the model for the concepts that will need
   * it, where the order is a fact about teaching rather than about the board.
   */
  needs: MechanicId[];
  /** Is this true of the board *right now*? Pure, over the state. */
  when: (state: GameState, side: Side) => boolean;
  /**
   * The square this concept is about, or null when it is not about one.
   *
   * The hint is drawn *at* its subject — a ring on the square and the card
   * beside it — so this is what decides where it lands. Pure and over the
   * board like `when`, which is what keeps the anchor gradeable by replaying a
   * log rather than by opening a browser.
   *
   * **`when` is derived from it wherever both exist** (see `about`), so a
   * concept that is true with nothing to point at is unreachable by
   * construction. Two predicates would drift, and the way they would fail is a
   * card floating beside empty ground.
   *
   * A concept about a *control* rather than a square has none, and is anchored
   * to the interface by `ui/hintTargets.ts` instead.
   */
  subject?: (state: GameState, side: Side) => number | null;
  /** Uses before it counts as learned. Usually 1; more for the fiddly ones. */
  learned: number;
  /** Heading in `content/rules/*.mdx` — the third tier. */
  anchor: string;
}

/** Every square this side has a piece on, in board order. */
function* ours(state: GameState, side: Side): Generator<number> {
  for (const sq of state.position.cellList) {
    const piece = pieceAt(state.position, sq);
    if (piece !== 0 && pieceColor(piece) === (side as Color)) yield sq;
  }
}

/**
 * **`when` asks about the situation, not about the button.**
 *
 * The obvious reading is to call `canMine` and `canLoadBelt` — the predicates
 * behind the refusal strings — and it does not work, because *mining and
 * handing off happen automatically as well as on demand*. The tick does both
 * for the player, stamps `minedAt`/`loadedAt` with the turn, and by the time
 * the player is looking at their own board those predicates answer
 * "already-mined-this-turn". Measured: the mining hint appeared on turn one and
 * never again, having taught nothing.
 *
 * So the test is whether the *concept* is available — is there a heap and
 * something that could work it — which is also the sentence the chip actually
 * says. Where a predicate is about a standing arrangement rather than a
 * once-a-turn action (`canPlaceBelt`) it is still exactly the right call.
 */
const harvesting = (state: GameState, sq: number): boolean =>
  harvestRate(state.position, sq) > 0;

/** Any metal of ours, in hand or on our own track. */
function ourMaterial(state: GameState, side: Side): boolean {
  for (const sq of ours(state, side)) {
    if (bufferTotal(state.industry, sq, "in") > 0) return true;
    if (bufferTotal(state.industry, sq, "out") > 0) return true;
  }
  for (const sq of liveSquares(state.industry.beltCargo)) {
    if (beltOwnerAt(state.industry, sq) === side) return true;
  }
  return false;
}

/** The first square of ours this holds for, in board order, or null. */
function firstOf(
  state: GameState,
  side: Side,
  test: (sq: number) => boolean
): number | null {
  for (const sq of ours(state, side)) if (test(sq)) return sq;
  return null;
}

/** Chebyshev — the same measure territory and the planner already use. */
const stepsBetween = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * A concept whose `when` is exactly "there is something to point at".
 *
 * One definition of the question, spread into both fields, because the two
 * readings failing apart is not a subtle bug: `when` true with no subject is a
 * hint card floating beside empty ground, and the reverse is a subject nobody
 * ever asks for. Where a concept is genuinely about a control rather than a
 * square it writes its own `when` and has no subject.
 */
const about = (
  subject: (state: GameState, side: Side) => number | null
): Pick<Concept, "subject" | "when"> => ({
  subject,
  when: (state, side) => subject(state, side) !== null
});

/**
 * The heap to walk a digger onto: the one nearest a digger of ours that is not
 * already standing on scrap.
 *
 * **The pile, not the pawn.** The hint is about where to go, and a ring round
 * the pawn leaves the player to work out the destination — which is the half
 * of the sentence they do not have yet.
 */
function heapToWork(state: GameState, side: Side): number | null {
  const diggers: number[] = [];
  for (const sq of ours(state, side)) {
    if (harvesting(state, sq) && heapAt(state.industry, sq) === 0) diggers.push(sq);
  }
  if (diggers.length === 0) return null;

  let best: number | null = null;
  let closest = Infinity;
  // `liveSquares` is sorted and the comparison is strict, so a tie keeps the
  // lower square — two clients drawing one board must ring the same heap.
  for (const sq of liveSquares(state.industry.heap)) {
    if (heapAt(state.industry, sq) <= 0) continue;
    for (const digger of diggers) {
      const steps = stepsBetween(sq, digger);
      if (steps < closest) {
        closest = steps;
        best = sq;
      }
    }
  }
  return best;
}

/** A digger of ours with ore in hand. */
const loadedDigger = (state: GameState, side: Side): number | null =>
  firstOf(
    state,
    side,
    (sq) => harvesting(state, sq) && bufferTotal(state.industry, sq, "in") > 0
  );

/** A part of ours, drawn or built — both are aimed the same way. */
function ourPart(state: GameState, side: Side): number | null {
  for (const sq of liveSquares(state.industry.beltDir)) {
    if (beltOwnerAt(state.industry, sq) === side) return sq;
  }
  for (const sq of liveSquares(state.industry.blueprintDir)) {
    if (blueprintOwnerAt(state.industry, sq) === side) return sq;
  }
  return null;
}

/** An oven of ours with something in reach worth cooking. */
const stockedOven = (state: GameState, side: Side): number | null =>
  firstOf(
    state,
    side,
    (sq) =>
      refinesAt(state.position, sq) &&
      bufferTotal(state.industry, sq, "in") > 0
  );

/** A foundry of ours with nothing chosen — but only once there is metal. */
const idleFoundry = (state: GameState, side: Side): number | null => {
  if (!ourMaterial(state, side)) return null;
  return firstOf(
    state,
    side,
    (sq) => isFoundry(machineType(state.position, sq)) && !targetAt(state.industry, sq)
  );
};

/** A foundry of ours that has a recipe and the metal to put toward it. */
const stockedFoundry = (state: GameState, side: Side): number | null =>
  firstOf(
    state,
    side,
    (sq) =>
      isFoundry(machineType(state.position, sq)) &&
      Boolean(targetAt(state.industry, sq)) &&
      bufferTotal(state.industry, sq, "in") > 0
  );

/** A pawn of ours facing somewhere its army would not have pointed it. */
function crookedPawn(state: GameState, side: Side): number | null {
  let knight = false;
  let crooked: number | null = null;
  for (const sq of ours(state, side)) {
    const type = pieceType(pieceAt(state.position, sq));
    if (type === KNIGHT) knight = true;
    if (
      crooked === null &&
      type === PAWN &&
      facingAt(state.position, sq) !== FORWARD_FACING[side]
    ) {
      crooked = sq;
    }
  }
  // The pawn is what the hint points at and the knight is what does the work,
  // so both have to exist before there is anything to say.
  return knight ? crooked : null;
}

/**
 * The curriculum.
 *
 * **`when` tests feasibility, not presence**, and it does it with the
 * predicates the UI already owns: `canMine`, `canLoadBelt`, `canPlaceBelt` are
 * the same knowledge from the other direction — *a refusal says why you
 * cannot; a hint says that you can.* A hint suggesting a belt on ground you do
 * not hold is worse than no hint, and the fix is not new logic, it is calling
 * the existing predicate.
 */
export const CURRICULUM: Concept[] = [
  {
    // First, because it is the only industrial act with no prerequisite and
    // the whole economy is downstream of it.
    //
    // A digger of ours not already on a pile, and a pile for it. Standing one
    // there is the whole of what the hint asks for, so somebody who has done
    // it is not shown it — no counter needed, the board says so.
    id: "mine",
    needs: [],
    learned: 1,
    anchor: "mining",
    ...about(heapToWork)
  },
  {
    // Only once something is actually holding ore — "pass it on" means nothing
    // to a player with nothing in hand.
    id: "handoff",
    needs: [],
    learned: 1,
    anchor: "hand-off",
    ...about(loadedDigger)
  },
  {
    // Ground you hold, and ore worth moving across it.
    id: "belts",
    needs: [],
    learned: 2,
    anchor: "belts",
    when: (state, side) => {
      let holding = false;
      for (const sq of ours(state, side)) {
        if (bufferTotal(state.industry, sq, "in") > 0) {
          holding = true;
          break;
        }
      }
      if (!holding) return false;
      for (const sq of state.position.cellList) {
        if (canPlaceBelt(state.position, state.industry, sq, side as Color) === null) {
          return true;
        }
      }
      return false;
    }
  },
  {
    // A part of ours exists, so which way it points is now a question the
    // player has. **Blueprints count**: a drawn part is aimed exactly as a
    // built one is, and redrawing a run is how a mis-aimed one is fixed
    // before it is ever paid for — so waiting for the iron to arrive would
    // teach aiming a turn after the moment it was most useful.
    id: "belt-aim",
    needs: [],
    learned: 1,
    anchor: "belts",
    ...about(ourPart)
  },
  {
    // An oven of ours, and something in reach worth cooking.
    id: "refine",
    needs: [],
    learned: 1,
    anchor: "refining",
    ...about(stockedOven)
  },
  {
    // A foundry with nothing chosen. Deliberately before `forge`: a recipe is
    // the decision, and forging is what happens once the metal arrives.
    //
    // **Gated on there being metal at all**, or this is the first thing a
    // player is ever told: every game opens with a rook and no recipe, so
    // "tell a rook what to save up for" fired on turn one of a board with
    // nothing to save and no way yet to get any. Driven, and it is exactly
    // §16.3.3's rule — do not teach foundry recipes to somebody who has no
    // use for one — applied to the material rather than to the rook.
    id: "recipe",
    needs: [],
    learned: 1,
    anchor: "forging",
    ...about(idleFoundry)
  },
  {
    // Only when a foundry is actually stocked — teaching the mint to somebody
    // with an empty hopper is teaching a button that refuses.
    id: "forge",
    needs: [],
    learned: 1,
    anchor: "forging",
    ...about(stockedFoundry)
  },
  {
    // The game's first unfamiliar idea, and it needs a knight *and* a pawn
    // pointing somewhere its side would not have pointed it.
    id: "re-aim",
    needs: [],
    learned: 1,
    anchor: "facing",
    ...about(crookedPawn)
  }
];

/** True when the profile has used this concept enough to count as learned. */
export const isLearned = (profile: Profile, concept: Concept): boolean =>
  (profile.counts[concept.id] ?? 0) >= concept.learned;

/**
 * Reasons the curriculum stays quiet this turn. §16.3.4: pacing yields to the
 * game, always. A suppressed turn is a turn it simply does not advance — it
 * has nowhere to be.
 */
export interface Quiet {
  /** In check: the worst possible moment to introduce a mechanic. */
  inCheck?: boolean;
  /** A card, an order or a dialogue already wants reading. */
  busy?: boolean;
  /** A Gambit or a fast-forward: the player is watching, not acting. */
  watching?: boolean;
  /** The player turned hints off, or is fluent enough not to want them. */
  off?: boolean;
}

const suppressed = (quiet: Quiet): boolean =>
  Boolean(quiet.inCheck || quiet.busy || quiet.watching || quiet.off);

/**
 * The concept to offer, or null.
 *
 * Order of the tests is the design: a concept the player has dismissed or
 * learned is never offered, a concept whose prerequisites are unmet is not
 * offered *yet*, and of what is left the board decides. First match wins, so
 * the table's order is the tie-break and nothing else.
 */
export function nextHint(
  profile: Profile,
  state: GameState,
  side: Side,
  quiet: Quiet = {},
  /** The table, so the mechanism can be graded without the shipped one. */
  table: readonly Concept[] = CURRICULUM
): Concept | null {
  if (suppressed(quiet)) return null;
  for (const concept of table) {
    if (isLearned(profile, concept)) continue;
    if (profile.dismissed?.includes(concept.id)) continue;
    // **Learned, not merely met.** A prerequisite somebody has done once is
    // not one they have understood, and `learned` is where that number lives.
    if (!concept.needs.every((id) => hasMet(profile, id))) continue;
    if (!concept.when(state, side)) continue;
    return concept;
  }
  return null;
}

/** Look a concept up by id, for a hint that was offered on an earlier turn. */
export const conceptById = (id: MechanicId): Concept | undefined =>
  CURRICULUM.find((concept) => concept.id === id);
